import { type Dispatch, type MutableRefObject, useEffect, useRef, useState } from "react";

/**
 * Set up an interval that keeps running for every interval.
 * Passing null to the interval delay will clear the interval.
 * @param callback
 * @param delayInMilliSecs
 */

export function useInterval(callback: () => void, delayInMilliSecs: number | null) {
  const savedCallback = useRef<() => void>();
  const savedIntervalId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    const tick = () => {
      savedCallback.current?.();
    };
    if (delayInMilliSecs !== null) {
      savedIntervalId.current = setInterval(tick, delayInMilliSecs);
      return () => savedIntervalId.current && clearInterval(savedIntervalId.current);
    }
    savedIntervalId.current && clearInterval(savedIntervalId.current);
  }, [delayInMilliSecs]);
}

/**
 * Similar to useState, it initializes the state value using the passed observedState,
 * but it also updates the state as the observedState changes by **reference**.
 * @param observedState
 * @returns [state: T, setState: Dispatch<React.SetStateAction<T>>]
 *
 *
 * This can replace a common pattern to update the state based on a prop value like the following.
 * ```
 * const { propValue } = props;
 * const [value, setValue] = useState(propValue);
 * useEffect(()=>{
 *      setValue(propValue)
 * }, [propValue])
 * ```
 */
export function useObserverState<T>(observedState: T) {
  const [state, setState] = useState<T>(observedState);
  useEffect(() => {
    setState(observedState);
  }, [observedState]);
  return [state, setState] as [T, Dispatch<React.SetStateAction<T>>];
}

export function useOutsideClick(ref: MutableRefObject<HTMLElement | null>, onClickOutside: () => void, enabled = true) {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, onClickOutside]);
}

export function usePrevious<T>(value: T) {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}
